// Phase 5A — Operations Foundation / Service Templates / Booking / Cutoffs /
// Free-Time. Extends the existing Operations Job workspace (JobDetail.jsx)
// with the real Booking lifecycle — never a second Job workspace. Sales
// never sees this panel's action buttons (server also hard-blocks via the
// `operations_booking` capability, this is UI convenience only).
import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusPill } from "@/components/shared/StatusPill";
import { AICard } from "@/components/shared/AICard";
import {
  useOpsEligibility, useOpsTemplate, useOpsNextAction, useOpsDocRequirements, useOpsBookings, useOpsCutoffs,
  useOpsFreeTime, useOpsAmendments, useOpsSuppliers, useApiMutation, useRole,
} from "@/api/hooks";
import { apiErrorMessage, isConflict } from "@/lib/apiError";
import { AlertTriangle, CalendarClock, Check, Ship, Loader2, PackageSearch, ShieldAlert, Upload, FileCheck2 } from "lucide-react";

const BOOKING_BUCKET = {
  DRAFT: "warn", REQUEST_READY: "warn", REQUESTED: "warn", SUPPLIER_RESPONSE_PENDING: "warn",
  CONFIRMED: "success", REJECTED: "danger", WAITLISTED: "warn", AMENDMENT_REQUIRED: "warn",
  CANCELLED: "danger", EXPIRED: "danger",
};

function invalidateAll(qc, jid, bid) {
  qc.invalidateQueries({ queryKey: ["job"] });
  qc.invalidateQueries({ queryKey: ["ops-eligibility", jid] });
  qc.invalidateQueries({ queryKey: ["ops-template", jid] });
  qc.invalidateQueries({ queryKey: ["ops-next-action", jid] });
  qc.invalidateQueries({ queryKey: ["ops-bookings", jid] });
  qc.invalidateQueries({ queryKey: ["ops-doc-reqs", jid] });
  if (bid) {
    qc.invalidateQueries({ queryKey: ["ops-booking", bid] });
    qc.invalidateQueries({ queryKey: ["ops-cutoffs", bid] });
    qc.invalidateQueries({ queryKey: ["ops-free-time", bid] });
    qc.invalidateQueries({ queryKey: ["ops-amendments", bid] });
  }
}

export default function OpsBookingPanel({ job }) {
  const role = useRole();
  const qc = useQueryClient();
  const canBook = role === "operations" || role === "management" || role === "admin";
  const jid = job.id;

  const { data: elig } = useOpsEligibility(jid);
  const { data: template } = useOpsTemplate(jid, elig?.eligible);
  const { data: nextAction } = useOpsNextAction(jid);
  const { data: docReqs } = useOpsDocRequirements(jid);
  const { data: bookings } = useOpsBookings(jid);
  const booking = (bookings || [])[0];
  const { data: cutoffs } = useOpsCutoffs(booking?.id);
  const { data: freeTime } = useOpsFreeTime(booking?.id);
  const { data: amendments } = useOpsAmendments(booking?.id);

  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [freeTimeOpen, setFreeTimeOpen] = useState(false);
  const mutate = useApiMutation();

  const startPlanning = async () => {
    try {
      await mutate.mutateAsync({ url: `/ops/jobs/${jid}/start-planning`, body: {} });
      invalidateAll(qc, jid);
      toast.success("Planning started — service template selected");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const readyForExecution = async () => {
    try {
      await mutate.mutateAsync({ url: `/ops/jobs/${jid}/ready-for-execution`, body: {} });
      invalidateAll(qc, jid);
      toast.success("Job is Ready for Execution");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (job.legacyRecord) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 flex items-center gap-2.5">
        <ShieldAlert className="h-4 w-4 shrink-0" />Legacy job — not controlled through Phase 5A Booking. Verification required before planning can start.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {nextAction && (
        <AICard title="Next action">
          <div className="font-medium text-slate-800">{nextAction.what}</div>
          <div className="text-slate-500 mt-0.5">{nextAction.why}</div>
          <div className="text-[11px] text-slate-400 mt-1">Due {nextAction.whenDue} · Then: {nextAction.then}</div>
        </AICard>
      )}

      {elig && !elig.eligible && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 space-y-1.5" data-testid="ops-eligibility-blockers">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle className="h-4 w-4" />Not yet eligible for operational planning</div>
          {elig.blockers.map((b, i) => <div key={i} className="text-[13px] text-amber-700">{b.code}: {b.message}</div>)}
        </div>
      )}

      {elig?.eligible && job.status === "OPERATIONS_ACCEPTED" && canBook && (
        <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={startPlanning} disabled={mutate.isPending} data-testid="ops-start-planning">
          {mutate.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PackageSearch className="h-3.5 w-3.5 mr-1.5" />}Start planning
        </Button>
      )}

      {template && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-template-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Service template · {template.key}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-slate-600">
            <div>Queue: <span className="font-medium text-slate-800">{template.responsibleQueue}</span></div>
            <div>Free time applicable: <span className="font-medium text-slate-800">{template.freeTimeApplicable ? "Yes" : "No"}</span></div>
          </div>
        </div>
      )}

      {["PLANNING", "BOOKING_REQUIRED"].includes(job.status) && !booking && canBook && (
        <BookingCreateCard jid={jid} template={template} onOpen={() => setNewBookingOpen(true)} />
      )}

      {booking && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-booking-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Booking · {booking.id}</div>
            <StatusPill status={booking.bookingStatus} bucket={BOOKING_BUCKET[booking.bookingStatus]} />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px] text-slate-600">
            <div>Carrier: <span className="font-medium text-slate-800">{booking.carrierName || "—"}</span></div>
            <div>Routing: <span className="font-medium text-slate-800">{booking.routing || `${booking.origin || ""} → ${booking.destination || ""}`}</span></div>
            <div>ETD: <span className="font-mono-num text-slate-800">{booking.etd || "—"}</span></div>
            <div>ETA: <span className="font-mono-num text-slate-800">{booking.eta || "—"}</span></div>
          </div>
          {booking.materialChangeApprovalIds?.length > 0 && booking.bookingStatus !== "REQUESTED" && booking.bookingStatus !== "CONFIRMED" && (
            <div className="mt-2 text-[12px] text-amber-700 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Material change — Approval required before the request record can be issued.</div>
          )}
          <div className="flex gap-2 mt-3">
            {booking.bookingStatus === "DRAFT" && canBook && (
              <SendRequestButton bookingId={booking.id} onDone={() => invalidateAll(qc, jid, booking.id)} />
            )}
            {["REQUESTED", "SUPPLIER_RESPONSE_PENDING", "WAITLISTED"].includes(booking.bookingStatus) && canBook && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirmOpen(true)} data-testid="ops-booking-confirm-open">
                <Check className="h-3.5 w-3.5 mr-1.5" />Confirm booking
              </Button>
            )}
            {booking.bookingStatus === "CONFIRMED" && template?.freeTimeApplicable && canBook && (
              <Button size="sm" variant="outline" className="h-8" onClick={() => setFreeTimeOpen(true)} data-testid="ops-free-time-open">
                <CalendarClock className="h-3.5 w-3.5 mr-1.5" />Record free-time terms
              </Button>
            )}
          </div>
          {booking.bookingStatus === "DRAFT" && canBook && (
            <div className="mt-2 text-[11px] text-slate-500">Issuing updates the booking workflow record. External email or carrier-portal delivery requires an approved provider integration.</div>
          )}
        </div>
      )}

      {cutoffs?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-cutoffs-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Cutoffs</div>
          <div className="space-y-1.5">
            {cutoffs.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-[13px]">
                <span className="text-slate-700">{c.type.replace(/_/g, " ")}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono-num text-slate-500">{c.plannedAt}</span>
                  <StatusPill status={c.status} bucket={c.status === "MISSED" ? "danger" : c.status === "AT_RISK" ? "warn" : c.status === "COMPLETED" ? "success" : undefined} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {freeTime && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-free-time-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Free time</div>
          {freeTime.computed.status === "FREE_TIME_RULE_NEEDS_REVIEW" ? (
            <div className="text-[13px] text-amber-700 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />Free-time rule needs review — {freeTime.computed.reason || "terms not fully known yet."}</div>
          ) : (
            <div className="text-[13px] text-slate-600 space-y-1">
              <div>Last free date: <span className="font-mono-num text-slate-800">{freeTime.computed.lastFreeDate}</span></div>
              <div>Days remaining: <span className="font-mono-num text-slate-800">{freeTime.computed.daysRemaining}</span></div>
              {freeTime.computed.estimatedExposure && (
                <div className="text-amber-700">Estimated exposure: {freeTime.computed.estimatedExposure.amount} {freeTime.computed.estimatedExposure.currency} — {freeTime.computed.estimatedExposure.label}</div>
              )}
            </div>
          )}
        </div>
      )}

      {amendments?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-amendments-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Amendment history</div>
          <div className="space-y-1.5 text-[12px] text-slate-600">
            {amendments.map((a) => (
              <div key={a.id}>{a.timestamp} — {a.reason} (by {a.changedBy})</div>
            ))}
          </div>
        </div>
      )}

      {docReqs?.documents?.length > 0 && canBook && (
        <PlanningChecklistCard jid={jid} rows={docReqs.documents} onDone={() => invalidateAll(qc, jid)} />
      )}

      {job.status === "BOOKED" && canBook && (
        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={readyForExecution} disabled={mutate.isPending} data-testid="ops-ready-for-execution">
          Mark Ready for Execution
        </Button>
      )}
      {job.status === "READY_FOR_EXECUTION" && <StatusPill status="Ready for Execution" bucket="success" />}

      <NewBookingDialog open={newBookingOpen} onOpenChange={setNewBookingOpen} jid={jid} job={job}
        onDone={() => invalidateAll(qc, jid)} />
      {booking && <ConfirmBookingDialog open={confirmOpen} onOpenChange={setConfirmOpen} bookingId={booking.id}
        onDone={() => invalidateAll(qc, jid, booking.id)} />}
      {booking && <FreeTimeDialog open={freeTimeOpen} onOpenChange={setFreeTimeOpen} bookingId={booking.id}
        onDone={() => invalidateAll(qc, jid, booking.id)} />}
    </div>
  );
}

// Item 9 (Aug 2026 Operations round) — Booking & Planning operational stage
// checklist. Rows come from GET /ops/jobs/{jid}/document-requirements
// (mode-aware — see services/operations_templates.py; irrelevant stages for
// this Job's mode are simply never in the list, no filtering needed here).
// Each row: Pending -> upload the corresponding document -> becomes "In
// Progress" (uploading never auto-completes it) -> explicit "Mark complete"
// (POST .../document-requirements/{name}/complete) persists Completed on
// the Job. Completion is blocked server-side (with a specific reason) if no
// matching document has been uploaded yet.
const CHECKLIST_BUCKET = { Pending: "warn", "In Progress": "default", Completed: "success" };
function PlanningChecklistCard({ jid, rows, onDone }) {
  const [busyRow, setBusyRow] = useState("");
  const fileRefs = useRef({});
  const qc = useQueryClient();

  const upload = async (name, file) => {
    if (!file) return;
    setBusyRow(`${name}-upload`);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("jobId", jid);
    fd.append("docType", name);
    fd.append("category", "PLANNING");
    try {
      await api.post("/documents/upload", fd);
      await qc.invalidateQueries({ queryKey: ["ops-doc-reqs", jid] });
      onDone();
      toast.success(`${name} uploaded — mark the stage complete when ready`);
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusyRow(""); }
  };

  const complete = async (name) => {
    setBusyRow(`${name}-complete`);
    try {
      await api.post(`/ops/jobs/${jid}/document-requirements/complete`, { name });
      await qc.invalidateQueries({ queryKey: ["ops-doc-reqs", jid] });
      onDone();
      toast.success(`${name} marked complete`);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not complete this stage")); }
    finally { setBusyRow(""); }
  };

  const done = rows.filter((r) => r.status === "Completed").length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ops-doc-requirements-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Booking &amp; planning checklist</div>
        <span className="text-[11px] font-semibold text-slate-500">{done}/{rows.length} complete</span>
      </div>
      <div className="space-y-2">
        {rows.map((d) => {
          const rowBusy = busyRow.startsWith(d.name);
          return (
            <div key={d.name} className="rounded-lg border border-slate-100 px-3 py-2" data-testid={`ops-checklist-${d.name.replace(/[^a-zA-Z]/g, "-")}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-medium text-slate-700">{d.name}</span>
                <StatusPill status={d.status} bucket={CHECKLIST_BUCKET[d.status]} />
              </div>
              {d.documentId && <div className="text-[11px] text-slate-400 mt-0.5">Uploaded {d.uploadedDate || "—"} · {d.verification || "Pending review"} · {d.source || "Upload"}</div>}
              {d.completedAt && <div className="text-[11px] text-emerald-600 mt-0.5 flex items-center gap-1"><FileCheck2 className="h-3 w-3" />Completed by {d.completedBy} · {new Date(d.completedAt).toLocaleString()}</div>}
              <div className="flex items-center gap-1.5 mt-1.5">
                <input ref={(el) => (fileRefs.current[d.name] = el)} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(d.name, f); }} />
                {d.status !== "Completed" && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={rowBusy}
                    onClick={() => fileRefs.current[d.name]?.click()} data-testid={`ops-checklist-upload-${d.name.replace(/[^a-zA-Z]/g, "-")}`}>
                    {busyRow === `${d.name}-upload` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                    {d.documentId ? "Re-upload" : "Upload document"}
                  </Button>
                )}
                {d.status !== "Completed" && (
                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={rowBusy || d.status === "Pending"}
                    onClick={() => complete(d.name)} data-testid={`ops-checklist-complete-${d.name.replace(/[^a-zA-Z]/g, "-")}`}
                    title={d.status === "Pending" ? "Upload the document above before this stage can be completed" : undefined}>
                    {busyRow === `${d.name}-complete` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}Mark complete
                  </Button>
                )}
              </div>
              {d.status === "Pending" && <div className="text-[11px] text-amber-600 mt-1">Upload the required document before this stage can be marked complete.</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BookingCreateCard({ jid, template, onOpen }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4 flex items-center justify-between">
      <div className="text-sm text-slate-500">No Booking yet{template ? ` for this ${template.key.replace(/_/g, " ")} Job` : ""}.</div>
      <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={onOpen} data-testid="ops-new-booking-btn"><Ship className="h-3.5 w-3.5 mr-1.5" />Create booking</Button>
    </div>
  );
}

function SendRequestButton({ bookingId, onDone }) {
  const mutate = useApiMutation();
  const send = async () => {
    try {
      await mutate.mutateAsync({ url: `/ops/bookings/${bookingId}/request`, body: { channel: "email" } });
      onDone(); toast.success("Booking request recorded as issued; external delivery is not confirmed");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={send} disabled={mutate.isPending} data-testid="ops-send-booking-request">
    {mutate.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}Issue request record
  </Button>;
}

function NewBookingDialog({ open, onOpenChange, jid, job, onDone }) {
  const { data: suppliers } = useOpsSuppliers(job.mode);
  const [supplierId, setSupplierId] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [etd, setEtd] = useState("");
  const [eta, setEta] = useState("");
  const mutate = useApiMutation();

  const submit = async () => {
    try {
      await mutate.mutateAsync({
        url: `/ops/jobs/${jid}/bookings`,
        body: { supplierId, carrierName: carrierName || undefined, origin: job.origin, destination: job.destination, etd: etd || undefined, eta: eta || undefined },
      });
      onOpenChange(false); onDone(); toast.success("Booking created (Draft) — prefilled from the Job");
    } catch (e) {
      if (isConflict(e)) toast.error("This changed since you loaded it — refresh and try again.");
      else toast.error(apiErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ops-new-booking-dialog">
        <DialogHeader><DialogTitle>Create booking</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-slate-400">Approved supplier / carrier</label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="ops-booking-supplier-select"><SelectValue placeholder="Select an approved supplier" /></SelectTrigger>
              <SelectContent>{(suppliers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-[11px] font-semibold uppercase text-slate-400">Carrier name (optional override)</label>
            <Input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} placeholder={job.origin && job.destination ? `${job.origin} → ${job.destination}` : ""} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[11px] font-semibold uppercase text-slate-400">ETD</label><Input type="datetime-local" value={etd} onChange={(e) => setEtd(e.target.value)} /></div>
            <div><label className="text-[11px] font-semibold uppercase text-slate-400">ETA</label><Input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!supplierId || mutate.isPending} data-testid="ops-booking-submit">Create booking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmBookingDialog({ open, onOpenChange, bookingId, onDone }) {
  const [evidenceType, setEvidenceType] = useState("");
  const [evidenceReference, setEvidenceReference] = useState("");
  const mutate = useApiMutation();
  const submit = async () => {
    try {
      await mutate.mutateAsync({ url: `/ops/bookings/${bookingId}/confirm`, body: { evidenceType, evidenceReference } });
      onOpenChange(false); onDone(); toast.success("Booking confirmed");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ops-confirm-booking-dialog">
        <DialogHeader><DialogTitle>Confirm booking — evidence required</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-slate-400">Evidence type</label>
            <Select value={evidenceType} onValueChange={setEvidenceType}>
              <SelectTrigger data-testid="ops-evidence-type-select"><SelectValue placeholder="How was this confirmed?" /></SelectTrigger>
              <SelectContent>
                {["CARRIER_API", "AIRLINE_RESPONSE", "SHIPPING_LINE_PDF", "AGENT_EMAIL", "COURIER_CONFIRMATION", "MANUAL_PORTAL", "MANUAL_CONFIRMATION"].map((t) => (
                  <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-slate-400">Reference / evidence</label>
            <Textarea value={evidenceReference} onChange={(e) => setEvidenceReference(e.target.value)}
              placeholder="e.g. Booked via external carrier portal — REF12345, or paste the carrier confirmation reference" />
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!evidenceType || !evidenceReference || mutate.isPending} data-testid="ops-confirm-submit">Confirm booking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FreeTimeDialog({ open, onOpenChange, bookingId, onDone }) {
  const [freeDays, setFreeDays] = useState("");
  const [startDate, setStartDate] = useState("");
  const [demurrageRate, setDemurrageRate] = useState("");
  const mutate = useApiMutation();
  const submit = async () => {
    try {
      await mutate.mutateAsync({
        url: `/ops/bookings/${bookingId}/free-time`,
        body: { freeTimeType: "DEMURRAGE", freeDays: freeDays ? Number(freeDays) : undefined, startTrigger: "DISCHARGE",
                startDate: startDate || undefined, demurrageRate: demurrageRate ? Number(demurrageRate) : undefined, currency: "AED" },
      });
      onOpenChange(false); onDone(); toast.success("Free-time terms recorded");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ops-free-time-dialog">
        <DialogHeader><DialogTitle>Record free-time terms</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[11px] font-semibold uppercase text-slate-400">Free days</label><Input type="number" value={freeDays} onChange={(e) => setFreeDays(e.target.value)} /></div>
          <div><label className="text-[11px] font-semibold uppercase text-slate-400">Start date (trigger)</label><Input type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><label className="text-[11px] font-semibold uppercase text-slate-400">Demurrage rate / day</label><Input type="number" value={demurrageRate} onChange={(e) => setDemurrageRate(e.target.value)} /></div>
          <div className="text-[11px] text-slate-400">Leave a field blank if genuinely unknown — the system will show "needs review", never a fabricated date.</div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={mutate.isPending} data-testid="ops-free-time-submit">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
